/* ── Identity Hub SSO ──────────────────────────────────────────────────────
   Mirrors the Purchase app's proven single-sign-on mechanism.

   On load, Siragu may be opened from the Identity Hub with the session tokens
   passed as URL query params (`access_token`, `refresh_token`, …). We capture
   those into localStorage, then strip them from the address bar so they don't
   linger in history. Every Siragu API call then attaches the bearer token via
   `authHeaders()`. The backend runs with AUTH_REQUIRED=false, so anonymous use
   still works when no token is present.
   ────────────────────────────────────────────────────────────────────────── */

export const IDENTITY_HUB_URL =
  process.env.REACT_APP_IDENTITY_HUB_URL || "http://localhost:7001";
export const IDENTITY_HUB_API =
  process.env.REACT_APP_IDENTITY_HUB_API || "http://localhost:7001/api/v1";

/* Keys we accept from the URL and persist verbatim into localStorage. */
const SSO_KEYS = [
  "access_token",
  "refresh_token",
  "institution_name",
  "institution_logo",
  "institution_code",
];

/* ── bootstrapSSO ──────────────────────────────────────────────────────────
   Reads SSO params from the URL, stores them, and strips them from the URL. */
export function bootstrapSSO() {
  if (typeof window === "undefined") return;
  let params;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    return;
  }

  let found = false;
  SSO_KEYS.forEach((key) => {
    const val = params.get(key);
    if (val) {
      try {
        localStorage.setItem(key, val);
      } catch {
        /* storage may be unavailable (private mode) — ignore */
      }
      params.delete(key);
      found = true;
    }
  });

  if (found) {
    // Rebuild the URL without the SSO params — no reload.
    const qs = params.toString();
    const newUrl =
      window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash;
    try {
      window.history.replaceState({}, document.title, newUrl);
    } catch {
      /* history API unavailable — non-fatal */
    }
  }
}

export function getToken() {
  try {
    return localStorage.getItem("access_token");
  } catch {
    return null;
  }
}

export function getRefreshToken() {
  try {
    return localStorage.getItem("refresh_token");
  } catch {
    return null;
  }
}

/* Attach the bearer token to a fetch's headers when we have one. */
export function authHeaders() {
  const token = getToken();
  return token ? { Authorization: "Bearer " + token } : {};
}

/* Decode a base64url segment (the JWT payload) safely. */
function decodeBase64Url(segment) {
  let s = segment.replace(/-/g, "+").replace(/_/g, "/");
  while (s.length % 4) s += "=";
  const decoded = atob(s);
  // Handle any UTF-8 bytes in the payload.
  try {
    return decodeURIComponent(
      decoded
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
  } catch {
    return decoded;
  }
}

/* ── getUser ───────────────────────────────────────────────────────────────
   Decode the access_token JWT payload into a lightweight user object. */
export function getUser() {
  const token = getToken();
  if (!token) return null;
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const claims = JSON.parse(decodeBase64Url(parts[1]));
    return {
      id: claims.sub,
      email: claims.email,
      full_name: claims.full_name || claims.name || claims.email,
      role: claims.role,
      institution_id: claims.institution_id,
    };
  } catch {
    return null;
  }
}

/* Decode the JWT `exp` (seconds since epoch) from the access token, or null. */
export function getTokenExp() {
  const token = getToken();
  if (!token) return null;
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const claims = JSON.parse(decodeBase64Url(parts[1]));
    return typeof claims.exp === "number" ? claims.exp : null;
  } catch {
    return null;
  }
}

/* True when we have no token, or it's expired / within `skewSeconds` of expiring. */
export function isTokenExpired(skewSeconds = 30) {
  const exp = getTokenExp();
  if (!exp) return true;
  return Date.now() / 1000 >= exp - skewSeconds;
}

/* Authed = a token that exists AND is not expired (not merely present). */
export function isAuthed() {
  return !!getToken() && !isTokenExpired(0);
}

/* Clear stored tokens and bounce to Identity Hub login, asking it to return
   here with fresh tokens (instant if the hub session is still alive). Shared
   by ensureFreshSession (silent refresh failed) and logout. */
function _clearSessionAndRedirectToLogin() {
  try {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user");
  } catch {
    /* ignore */
  }
  try {
    const back = encodeURIComponent(window.location.origin + window.location.pathname);
    window.location.href = `${IDENTITY_HUB_URL}/login?redirect=${back}`;
  } catch {
    window.location.href = IDENTITY_HUB_URL;
  }
}

/* ── Silent token refresh ─────────────────────────────────────────────────
   The access token is short-lived. The backend can't distinguish "no token"
   from "expired token" — get_optional_principal() treats both as anonymous —
   so a chat tab left open past expiry silently drops to "please sign in"
   answers with no visible error, even though the refresh_token could have
   kept the session alive. We refresh proactively via Identity Hub's
   POST /auth/refresh, both once at load (ensureFreshSession) and periodically
   while the tab stays open (startTokenRefreshLoop). */

/* Exchange the stored refresh_token for a new access_token. The backend
   rotates refresh tokens on every use (old one is revoked), so the response's
   refresh_token MUST replace the stored one. Returns true on success, false
   if there's no refresh_token or it was rejected (expired/invalid/reused). */
export async function refreshAccessToken() {
  const refresh = getRefreshToken();
  if (!refresh) return false;
  try {
    const res = await fetch(`${IDENTITY_HUB_API}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refresh }),
    });
    if (!res.ok) return false;
    const data = await res.json();
    const payload = data && data.payload;
    if (!data?.is_success || !payload?.access_token) return false;
    localStorage.setItem("access_token", payload.access_token);
    if (payload.refresh_token) {
      localStorage.setItem("refresh_token", payload.refresh_token);
    }
    return true;
  } catch {
    return false; // network error — treat like any other refresh failure
  }
}

/* Called once at startup, before the app renders. If we were signed in but
   the token has expired, try a silent refresh first — only clear the session
   and bounce to Identity Hub login if that refresh itself fails. Returns true
   if the app may render normally, false if a redirect was triggered (caller
   should skip rendering the about-to-unload page). */
export async function ensureFreshSession() {
  const token = getToken();
  if (!token) return true; // never signed in — anonymous mode, leave it alone
  if (!isTokenExpired(0)) return true; // still valid
  if (await refreshAccessToken()) return true; // silently renewed
  _clearSessionAndRedirectToLogin();
  return false;
}

let _refreshLoopStarted = false;

/* Keep the session alive while the tab stays open: every `intervalMs`, renew
   the access token BEFORE it actually expires (once within `skewSeconds` of
   expiry) so a long conversation never silently drops to anonymous mid-
   session. Safe to call more than once — only ever starts one interval. */
export function startTokenRefreshLoop(intervalMs = 60_000, skewSeconds = 180) {
  if (_refreshLoopStarted || typeof window === "undefined") return;
  _refreshLoopStarted = true;
  setInterval(() => {
    const token = getToken();
    if (!token) return; // anonymous — nothing to refresh
    if (isTokenExpired(skewSeconds)) {
      refreshAccessToken();
    }
  }, intervalMs);
}

/* ── logout ────────────────────────────────────────────────────────────────
   Clear the session and hand back to the Identity Hub login. */
export function logout() {
  try {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user");
  } catch {
    /* ignore storage errors */
  }
  window.location.href = IDENTITY_HUB_URL;
}
